/**
 * MifeLife v2 — minimal App. (25 May 2026)
 *
 * STRATEGIC PIVOT: single brand, three modules, one flow.
 * "AI yol haritanı çizer, gerçek mentörler hedeflerine ulaştırır."
 *
 * Legacy (sirket-doktoru, advisor, showcase, sales, yeni-isletmeler, araclar,
 * yasam-analizi, life dashboard) lives in App.legacy.js for rollback. Preview
 * surface is restricted to the v2 routes below — production deploy is gated
 * behind the user's explicit "Save & Deploy".
 */
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { Toaster } from "./components/ui/sonner";

// V2 pages
import V2LandingPage from "./pages/v2/V2LandingPage";
import MaintenancePage from "./pages/MaintenancePage";
import V2AnalysisPage from "./pages/v2/V2AnalysisPage";
import V2CompanyAnalysisPage from "./pages/v2/V2CompanyAnalysisPage";
import V2SharedReportPage from "./pages/v2/V2SharedReportPage";
import V2AboutPage from "./pages/v2/V2AboutPage";
import V2ContactPage from "./pages/v2/V2ContactPage";
import V2PrivacyPage from "./pages/v2/V2PrivacyPage";
import V2TermsPage from "./pages/v2/V2TermsPage";
import V2KvkkPage from "./pages/v2/V2KvkkPage";
import V2MentorsPage from "./pages/v2/V2MentorsPage";
import V2MentorDetailPage from "./pages/v2/V2MentorDetailPage";
import V2PricingPage from "./pages/v2/V2PricingPage";
import { V2LoginPage, V2RegisterPage } from "./pages/v2/V2AuthPages";
import {
  V2DashboardLayout, V2DashboardHome, V2CalendarPage, V2GoalsPage,
  V2AssistantPage, V2MyMentorPage, V2SettingsPage,
} from "./pages/v2/V2Dashboard";
import { V2BlogIndexPage, V2BlogPostPage } from "./pages/v2/V2BlogPages";
import V2MentorApplyPage from "./pages/v2/V2MentorApplyPage";
import { V2MentorLoginPage, V2MentorClaimPage } from "./pages/v2/V2MentorAuthPages";
import V2MentorPanelPage from "./pages/v2/V2MentorPanelPage";
import V2MentorOnboardingPage from "./pages/v2/V2MentorOnboardingPage";
import V2SessionRoomPage from "./pages/v2/V2SessionRoomPage";
import V2AdminMentorApplicationsPage from "./pages/v2/V2AdminMentorApplicationsPage";
import V2AuthCallbackPage from "./pages/v2/V2AuthCallbackPage";
import {
  V2AdminLayout, V2AdminDashboard, V2AdminMentorsPage,
  V2AdminUsersPage, V2AdminSessionsPage, V2AdminApplicationsPage, V2AdminSettingsPage,
  V2AdminReportsPage, V2AdminContactPage, V2AdminOnboardingPage,
  V2AdminDisputesPage, V2AdminPayoutsPage, V2AdminCampaignsPage,
} from "./pages/v2/admin/V2Admin";

// Legal (kept; cheap surface, GDPR/KVKK)
import LegalPrivacyPage from "./pages/legal/PrivacyPage";
import LegalTermsPage from "./pages/legal/TermsPage";

// Payment callback (iyzico) — re-used by v2 pricing
import PaymentCallbackPage from "./pages/PaymentCallbackPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentFailPage from "./pages/PaymentFailPage";

function NotFound() {
  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="v2-404">
      <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">404</div>
      <h1 className="text-3xl font-semibold tracking-tight mb-3">Sayfa bulunamadı.</h1>
      <p className="text-sm text-slate-400 mb-6">Aradığın sayfa kaldırıldı ya da hiç var olmadı.</p>
      <a href="/" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition">Ana sayfaya dön</a>
    </div>
  );
}

function App() {
  // ------------------------------------------------------------------
  // MAINTENANCE GATE
  // Enabled when REACT_APP_MAINTENANCE_MODE === "true" (set in production
  // frontend/.env). Owner can bypass once per browser by appending
  //   ?bypass=<REACT_APP_MAINTENANCE_BYPASS>
  // to any URL — the value is then cached in localStorage.
  // ------------------------------------------------------------------
  if (typeof window !== 'undefined') {
    const maintenanceOn = String(process.env.REACT_APP_MAINTENANCE_MODE || '').toLowerCase() === 'true';
    if (maintenanceOn) {
      const expected = process.env.REACT_APP_MAINTENANCE_BYPASS || '';
      try {
        const url = new URL(window.location.href);
        const fromQuery = url.searchParams.get('bypass');
        if (expected && fromQuery && fromQuery === expected) {
          localStorage.setItem('mifelife_bypass', expected);
        }
      } catch (e) { /* ignore URL parse errors */ }
      const cached = (typeof localStorage !== 'undefined' ? localStorage.getItem('mifelife_bypass') : '') || '';
      const bypassed = expected && cached === expected;
      if (!bypassed) {
        return <MaintenancePage />;
      }
    }
  }

  // Synchronous OAuth fragment detection — must run BEFORE Routes mount.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
    return (
      <BrowserRouter>
        <AuthProvider>
          <V2AuthCallbackPage />
        </AuthProvider>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <Toaster />
          <Routes>
            {/* ---------- PUBLIC ---------- */}
            <Route path="/"             element={<V2LandingPage />} />
            <Route path="/analiz"       element={<V2AnalysisPage />} />
            <Route path="/sirket"       element={<V2CompanyAnalysisPage />} />
            <Route path="/r/:token"      element={<V2SharedReportPage />} />
            <Route path="/hakkinda"      element={<V2AboutPage />} />
            <Route path="/iletisim"      element={<V2ContactPage />} />
            <Route path="/privacy"       element={<V2PrivacyPage />} />
            <Route path="/terms"         element={<V2TermsPage />} />
            <Route path="/kvkk"          element={<V2KvkkPage />} />
            <Route path="/mentorler"    element={<V2MentorsPage />} />
            <Route path="/m/:slug"       element={<V2MentorDetailPage />} />
            <Route path="/mentor/:slug"  element={<V2MentorDetailPage />} />
            <Route path="/fiyatlandirma" element={<V2PricingPage />} />
            <Route path="/blog"         element={<V2BlogIndexPage />} />
            <Route path="/blog/:slug"   element={<V2BlogPostPage />} />
            <Route path="/mentor-basvuru" element={<V2MentorApplyPage />} />
            <Route path="/mentor/giris"   element={<V2MentorLoginPage />} />
            <Route path="/mentor/davet"   element={<V2MentorClaimPage />} />
            <Route path="/mentor/panel"   element={<V2MentorPanelPage />} />
            <Route path="/mentor/onboarding" element={<V2MentorOnboardingPage />} />

            {/* Auth */}
            <Route path="/giris"        element={<V2LoginPage />} />
            <Route path="/kayit"        element={<V2RegisterPage />} />
            <Route path="/auth/callback" element={<V2AuthCallbackPage />} />

            {/* ---------- ADMIN ---------- */}
            <Route path="/admin" element={<V2AdminLayout />}>
              <Route index                element={<V2AdminDashboard />} />
              <Route path="mentorler"     element={<V2AdminMentorsPage />} />
              <Route path="kullanicilar"  element={<V2AdminUsersPage />} />
              <Route path="rezervasyonlar" element={<V2AdminSessionsPage />} />
              <Route path="raporlar"      element={<V2AdminReportsPage />} />
              <Route path="onboarding"    element={<V2AdminOnboardingPage />} />
              <Route path="campaign"      element={<V2AdminCampaignsPage />} />
              <Route path="disputes"      element={<V2AdminDisputesPage />} />
              <Route path="payouts"       element={<V2AdminPayoutsPage />} />
              <Route path="mesajlar"      element={<V2AdminContactPage />} />
              <Route path="basvurular"    element={<V2AdminApplicationsPage />} />
              <Route path="ayarlar"       element={<V2AdminSettingsPage />} />
            </Route>
            {/* Legacy alias for the older mentor-applications-only screen */}
            <Route path="/admin/mentor-basvurulari" element={<Navigate to="/admin/basvurular" replace />} />

            {/* ---------- AUTHENTICATED APP ---------- */}
            <Route path="/app/seans/:id" element={<V2SessionRoomPage />} />
            <Route path="/app" element={<V2DashboardLayout />}>
              <Route index           element={<V2DashboardHome />} />
              <Route path="takvim"   element={<V2CalendarPage />} />
              <Route path="hedefler" element={<V2GoalsPage />} />
              <Route path="mentorum" element={<V2MyMentorPage />} />
              <Route path="ai-asistan" element={<V2AssistantPage />} />
              <Route path="ayarlar"  element={<V2SettingsPage />} />
            </Route>

            {/* iyzico return paths (existing backend flow) */}
            <Route path="/payment/callback" element={<PaymentCallbackPage />} />
            <Route path="/payment/success"  element={<PaymentSuccessPage />} />
            <Route path="/payment/fail"     element={<PaymentFailPage />} />

            {/* Legal */}
            <Route path="/privacy" element={<LegalPrivacyPage />} />
            <Route path="/terms"   element={<LegalTermsPage />} />
            <Route path="/kvkk"    element={<LegalPrivacyPage />} />

            {/* ---------- LEGACY REDIRECTS (preview) ----------
                Send any old surface to / so visitors don't 404 during the pivot. */}
            <Route path="/sirket-doktoru/*"     element={<Navigate to="/analiz" replace />} />
            <Route path="/yasam-analizi/*"      element={<Navigate to="/analiz" replace />} />
            <Route path="/yeni-isletmeler/*"    element={<Navigate to="/" replace />} />
            <Route path="/araclar/*"            element={<Navigate to="/" replace />} />
            <Route path="/showcase/*"           element={<Navigate to="/" replace />} />
            <Route path="/sales/*"              element={<Navigate to="/fiyatlandirma" replace />} />
            <Route path="/en/*"                 element={<Navigate to="/" replace />} />
            <Route path="/de/*"                 element={<Navigate to="/" replace />} />
            <Route path="/dashboard"            element={<Navigate to="/app" replace />} />
            <Route path="/dashboard/*"          element={<Navigate to="/app" replace />} />
            <Route path="/login"                element={<Navigate to="/giris" replace />} />
            <Route path="/register"             element={<Navigate to="/kayit" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
